import { supabase } from '../lib/supabase';
import type { Message } from '../types';

/**
 * Persists an emoji reaction on message `index` of a conversation via the
 * `set_message_reaction` RPC (an atomic jsonb_set on conversations.messages
 * gated by the same RLS update policies as normal sends), and optimistically
 * applies the same change to local state through `setMessages`.
 *
 * Clicking the currently-active emoji again clears the reaction.
 *
 * `conversationId` may be null/undefined for chats with no durable row yet
 * (or ever) — in that case the reaction still updates local state but is not
 * persisted.
 */
export function useMessageReactions(
  conversationId: string | null | undefined,
  setMessages: (updater: (prev: Message[]) => Message[]) => void
) {
  async function react(index: number, emoji: string) {
    let nextReaction: string | undefined;
    setMessages((prev) => {
      const target = prev[index];
      if (!target) return prev;
      nextReaction = target.reaction === emoji ? undefined : emoji;
      const updated = [...prev];
      updated[index] = { ...target, reaction: nextReaction };
      return updated;
    });

    if (!conversationId) return;
    const { error } = await supabase.rpc('set_message_reaction', {
      p_conv_id: conversationId,
      p_index: index,
      p_reaction: nextReaction ?? null,
    });
    if (error) console.error('set_message_reaction failed:', error);
  }

  return { react };
}
